import type { TurboFrameLoadEvent } from "@hotwired/turbo";
import { Application } from "@hotwired/stimulus";
import { CoreWebStimulusControllers } from "../../../Libraries/CoreWeb/CoreWeb/Features/Shared/Generated/CoreWebStimulusControllers";
import { StimulusControllers } from "./Generated/StimulusControllers";
import { logError } from "../../../Libraries/CoreWeb/CoreWeb/Features/Shared/logging";
import { isNavigationAbort } from "./NavigationAbort";

import "maplibre-gl/dist/maplibre-gl.css";
import "./site.css";
import "../Profile/Profile.css";
import PreferredLanguagesController from "../Profile/Languages/PreferredLanguagesController";
import AddLanguagesController from "../Profile/Languages/AddLanguagesController";
import { PreferredLanguageNavigation } from "./Localization/PreferredLanguageNavigation";
import "./ResponsiveMediaSize/ResponsiveMediaSize.css";
import "../Destinations/DestinationListPages.css";
import "../Destinations/AllTours/AllTours.css";
import "./HorizontalRail/HorizontalRail.css";
import "./ResponsiveImageHeader/ResponsiveImageHeader.css";
import "./BottomNavigation/BottomNavigation.css";
import "./ThumbnailFeatured/ThumbnailFeatured.css";
import "./PremiumTourBanner/PremiumTourBanner.css";
import "./DestinationNavigationCard/DestinationNavigationCard.css";
import "../Destinations/Tours/Detail/TourDetail.css";
import "../Destinations/Detail/Explore/TouristCardRedirect.css";
import "./ContentShare/ContentShare.css";
import "../WebIntegrations/FullScreen/IframeFullScreen.css";


import BodyController from "./BodyController";
import BranchOutcomeArrivalController from "../Branch/BranchOutcomeArrivalController";
import AnalyticsConsentController from "./AnalyticsConsent/AnalyticsConsentController";
import DestinationDetailController from "../Destinations/Detail/DestinationDetailController";
import DestinationExploreController from "../Destinations/Detail/Explore/DestinationExploreController";
import TouristCardRedirectController from "../Destinations/Detail/Explore/TouristCardRedirectController";
import DestinationSightsController from "../Destinations/Sights/DestinationSightsController";
import DestinationToursController from "./DestinationTours/DestinationToursController";
import HomeController from "../Home/HomeController";
import NativeAppInstallController from "./NativeAppInstall/NativeAppInstallController";
import PersistentAudioPlayerController from "./PersistentAudioPlayer/PersistentAudioPlayerController";
import GpsTriggeringController from "./GpsTriggering/GpsTriggeringController";
import PoiDetailController from "../Destinations/Places/Detail/PoiDetailController";
import PoiVideoController from "./PoiVideo/PoiVideoController";
import ResponsiveImageHeaderController from "./ResponsiveImageHeader/ResponsiveImageHeaderController";
import MapPreviewController from "./MapPreview/MapPreviewController";
import SmartGuideNavigationHistoryController from "./NavigationHistory/SmartGuideNavigationHistoryController";
import { installInstantTurboScroll, TurboScrollView } from "./NavigationHistory/TurboScrollRestoration";
import PermanentMapController from "./PermanentMap/PermanentMapController";
import SmartGuideRecentGuideController from "./RecentlyOpened/SmartGuideRecentGuideController";
import TourTrayController from "./TourTray/TourTrayController";
import ThumbnailDistanceController from "./ThumbnailDistance/ThumbnailDistanceController";
import TurboFrameController from "../../../Libraries/CoreWeb/CoreWeb/Features/Shared/TagHelpers/TurboFrame/TurboFrameController";
import ViewOrEditFormController from "../../../Libraries/CoreWeb/CoreWeb/Features/Shared/TagHelpers/ViewOrEditForm/ViewOrEditFormController";
import HintController from "../../../Libraries/CoreWeb/CoreWeb/Features/Shared/TagHelpers/Hint/HintController";
import { attachSeoAnalytics } from "./Analytics/SeoAnalytics";
import ContentShareController from "./ContentShare/ContentShareController";
import WebIntegrationNavigationGuardController from "../WebIntegrations/Navigation/WebIntegrationNavigationGuardController";
import IframeFullScreenButtonController from "../WebIntegrations/FullScreen/IframeFullScreenButtonController";
import { getWidgetFullScreenBridge } from "../WebIntegrations/FullScreen/WidgetFullScreenBridge";
import { smartGuideFrontendSettings, webTranslations } from './webTranslations';

getWidgetFullScreenBridge(window, document, {
    isWidget: smartGuideFrontendSettings.integrationDeliveryKind === 'Widget',
    dialogLabel: () => webTranslations.widgetFullScreenDialogLabel
});

const application = document.documentElement.hasAttribute("data-smartguide-unsupported-browser")
    ? undefined
    : Application.start();

if (application) {
    installInstantTurboScroll((window as Window & { Turbo?: { session?: { view?: TurboScrollView } } }).Turbo?.session?.view);
    application.register(StimulusControllers.analyticsConsent, AnalyticsConsentController);
    application.register(StimulusControllers.body, BodyController);
    application.register(StimulusControllers.branchOutcomeArrival, BranchOutcomeArrivalController);
    application.register(StimulusControllers.destinationDetail, DestinationDetailController);
    application.register(StimulusControllers.destinationExplore, DestinationExploreController);
    application.register(StimulusControllers.touristCardRedirect, TouristCardRedirectController);
    application.register(StimulusControllers.destinationSights, DestinationSightsController);
    application.register(StimulusControllers.contentShare, ContentShareController);
    application.register(StimulusControllers.iframeFullScreenButton, IframeFullScreenButtonController);
    application.register("destination-tours", DestinationToursController);
    application.register("home", HomeController);
    application.register("preferred-languages", PreferredLanguagesController);
    application.register("add-languages", AddLanguagesController);
    new PreferredLanguageNavigation().connect();
    application.register(StimulusControllers.nativeAppInstallDialog, NativeAppInstallController);
    application.register("smartguide-recent-guide", SmartGuideRecentGuideController);
    application.register(StimulusControllers.persistentAudioPlayer, PersistentAudioPlayerController);
    application.register(StimulusControllers.gpsTriggering, GpsTriggeringController);
    application.register(StimulusControllers.poiDetail, PoiDetailController);
    application.register(StimulusControllers.poiVideo, PoiVideoController);
    application.register(StimulusControllers.responsiveImageHeader, ResponsiveImageHeaderController);
    application.register(StimulusControllers.smartGuideMapPreview, MapPreviewController);
    application.register("smartguide-navigation-history", SmartGuideNavigationHistoryController);
    application.register(StimulusControllers.smartGuidePermanentMap, PermanentMapController);
    application.register(StimulusControllers.smartGuideTourTray, TourTrayController);
    application.register(StimulusControllers.thumbnailDistance, ThumbnailDistanceController);
    application.register(CoreWebStimulusControllers.turboFrame, TurboFrameController);
    application.register(CoreWebStimulusControllers.viewOrEditForm, ViewOrEditFormController);
    application.register(CoreWebStimulusControllers.hint, HintController);
    application.register(StimulusControllers.webIntegrationNavigationGuard, WebIntegrationNavigationGuardController);

    attachValidationOnTurboLoad();
    scrollToFragmentIdentifierOnTurboLoad();
    handleErrorsGlobally(application);
    attachSeoAnalytics(document, window);

    console.log("App reloaded");
}


function attachValidationOnTurboLoad() {
    document.addEventListener("turbo:load", () => {
        $.validator.unobtrusive.parse(document);
    });

    document.addEventListener("turbo:frame-load", (event: TurboFrameLoadEvent) => {
        const target = event.target as Element;
        $.validator.unobtrusive.parse(target);
    });
}

function scrollToFragmentIdentifierOnTurboLoad() {
    document.addEventListener("turbo:load", () => {
        scrollFragmentIdentifierElementIntoView();
    });

    document.addEventListener("turbo:frame-load", (event: TurboFrameLoadEvent) => {
        scrollFragmentIdentifierElementIntoView();
    });

    function scrollFragmentIdentifierElementIntoView() {
        if (window.location.hash) {
            const el = document.querySelector(window.location.hash);
            if (el) {
                el.scrollIntoView();
            }
        }
    }
}

function handleErrorsGlobally(application: Application) {
    
    window.onerror = async (message, source, lineno, colno, error) => {
        await logError(error ?? message, {source, lineno, colno});
    };

    window.addEventListener("unhandledrejection", async (event: PromiseRejectionEvent) => {
        if (isNavigationAbort(event.reason)) {
            event.preventDefault();
            return;
        }

        await logError(event.reason);
    });

    application.handleError = async (error: Error, message: string, detail: object) => {
        console.error(error, message, detail);

        // @ts-ignore
        let stimulusIdentifier = detail?.identifier;
        // @ts-ignore
        let elementId = detail?.element?.id;

        await logError(error, {stimulusIdentifier, elementId});
    }
}
